var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Tooltip } from '@robinpowered/ui-kit';
import { useFormContext, useWatch } from 'react-hook-form';
import { ComposerMode } from '../../types/index';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
import { useConfig } from '../../context/ConfigContext';
import { useEventApi } from '../../hooks/useEvent/useEventApi';
import { parseConfirmationWindow, shouldShowPreConfirmCheckbox, } from '../../utils/preConfirm';
export var PreConfirmCheckbox = function (_a) {
    var disabled = _a.disabled;
    var t = useTranslation('meetingRooms').t;
    var _b = useFormContext(), setValue = _b.setValue, dirtyFields = _b.formState.dirtyFields;
    var _c = useEventComposerGlobalContext(), composerMode = _c.composerMode, locationId = _c.locationId, onError = _c.onError;
    var _d = useConfig(), accessToken = _d.accessToken, environmentUrls = _d.environmentUrls, tenantId = _d.tenantId, userContext = _d.userContext;
    var fetchLocationSettings = useEventApi({
        onError: onError,
        accessToken: accessToken,
        apiUrl: environmentUrls.apiUrl,
        baseApiUrl: environmentUrls.baseApiUrl,
        tenantId: tenantId,
        currentOrg: userContext.currentOrg,
    }).fetchLocationSettings;
    var _e = __read(useWatch({
        name: ['spaces', 'preConfirm', 'startTime', 'recurrenceRule'],
    }), 4), spaces = _e[0], preConfirm = _e[1], startTime = _e[2], recurrenceRule = _e[3];
    var _f = __read(useState(undefined), 2), locationSettings = _f[0], setLocationSettings = _f[1];
    // Stay hidden until location settings for a real location have loaded.
    var _g = __read(useState(false), 2), locationSettingsReady = _g[0], setLocationSettingsReady = _g[1];
    var resolvedLocationId = useMemo(function () {
        var _a;
        if (locationId)
            return Number(locationId);
        var fromSpace = (_a = spaces === null || spaces === void 0 ? void 0 : spaces[0]) === null || _a === void 0 ? void 0 : _a.locationId;
        return fromSpace != null ? Number(fromSpace) : undefined;
    }, [locationId, spaces]);
    useEffect(function () {
        if (!resolvedLocationId || Number.isNaN(resolvedLocationId)) {
            setLocationSettings(undefined);
            return;
        }
        var cancelled = false;
        setLocationSettingsReady(false);
        fetchLocationSettings(resolvedLocationId)
            .then(function (settings) {
            if (!cancelled)
                setLocationSettings(settings);
        })
            .finally(function () {
            if (!cancelled)
                setLocationSettingsReady(true);
        });
        return function () {
            cancelled = true;
        };
    }, [fetchLocationSettings, resolvedLocationId]);
    var isCreateMode = composerMode === ComposerMode.CREATE;
    var isRecurring = !!recurrenceRule;
    var confirmationWindow = parseConfirmationWindow(locationSettings === null || locationSettings === void 0 ? void 0 : locationSettings['event-confirmation-window']);
    // Eligibility is only knowable after settings resolve. Until then, leave the
    // form default alone — !isVisible during load is "unknown", not "ineligible".
    var eligibilityKnown = isCreateMode && locationSettingsReady && !!startTime;
    var isEligible = eligibilityKnown &&
        shouldShowPreConfirmCheckbox({
            isEditing: false,
            confirmationWindow: confirmationWindow,
            startedAt: startTime,
            eventConfirmation: false,
            spaces: spaces !== null && spaces !== void 0 ? spaces : [],
        });
    var isVisible = isEligible;
    var canOptIn = isEligible && !isRecurring;
    // Only mutate once eligibility is known:
    // - known ineligible / recurring → force false (do not invent bypass)
    // - known eligible + untouched → keep/restore true
    // - still loading → no-op (preserves form default)
    useEffect(function () {
        if (!isCreateMode || !eligibilityKnown) {
            return;
        }
        if (!canOptIn) {
            setValue('preConfirm', false, { shouldDirty: false });
            return;
        }
        if (!dirtyFields.preConfirm) {
            setValue('preConfirm', true, { shouldDirty: false });
        }
    }, [
        canOptIn,
        dirtyFields.preConfirm,
        eligibilityKnown,
        isCreateMode,
        setValue,
    ]);
    if (!isVisible) {
        return null;
    }
    var checkboxDisabled = disabled || isRecurring;
    var checkbox = (_jsx(Checkbox, { checked: !!preConfirm, onChange: function (e) {
            setValue('preConfirm', e.target.checked, { shouldDirty: true });
        }, disabled: checkboxDisabled, style: { display: 'flex', alignItems: 'center' }, children: t('create_event.do_not_require_check_in') }));
    if (!isRecurring) {
        return checkbox;
    }
    return (_jsx(Tooltip, { title: t('create_event.pre_confirm_recurring_not_available'), children: _jsx("span", { children: checkbox }) }));
};
